import type { Axios, AxiosInterceptorManager, AxiosRequestConfig, AxiosResponse } from 'axios';

export type ApiTransport = 'http' | 'ws';

export enum ApiException {
  ValidationException = 'ValidationException',
  ValidationError = 'ValidationError',
  UnhandledError = 'UnhandledError',
  InternalError = 'InternalError',
  ServiceError = 'ServiceError',
}

export interface HttpConfig {
  baseURL: string;
  transformRequest?: Function[];
  transformResponse?: Function[];
  paramsSerializer?: Function;
  headers?: Record<string, string>;
  timeout?: number;
  withCredentials?: boolean;
}

export interface WebSocketConfig {
  connectionUrl: string;
  reconnectAttempts?: number;
}

export type AxiosInterceptors = Axios['interceptors'];
export type AxiosInterceptorType = keyof AxiosInterceptors;
export type AxiosRequestInterceptorTuple = Parameters<
  AxiosInterceptorManager<AxiosRequestConfig>['use']
>;
export type AxiosResponseInterceptorTuple = Parameters<
  AxiosInterceptorManager<AxiosResponse>['use']
>;

export interface AxiosInterceptorOptions {
  request?: AxiosRequestInterceptorTuple[];
  response?: AxiosResponseInterceptorTuple[];
}

export interface IBaseService {
  setHeader: (key: string, value: string) => void;

  get: <T>(url: string, config?: AxiosRequestConfig) => Promise<T>;

  delete: <T>(url: string, config?: AxiosRequestConfig) => Promise<T>;

  head: <T>(url: string, config?: AxiosRequestConfig) => Promise<T>;

  options: <T>(url: string, config?: AxiosRequestConfig) => Promise<T>;

  post: <T>(url: string, payload?: unknown, config?: AxiosRequestConfig) => Promise<T>;

  put: <T>(url: string, payload?: unknown, config?: AxiosRequestConfig) => Promise<T>;

  patch: <T>(url: string, payload?: unknown, config?: AxiosRequestConfig) => Promise<T>;

  request: <T>(config: AxiosRequestConfig) => Promise<T>;
}

export type Constructor = new (...args: any[]) => any;

export interface ResultsResponse<T> {
  results: T[];
  count: number;
  next: string | null;
  previous: string | null;
}

export interface RequestCommonParams {
  limit?: number;
  offset?: number;
}

export interface DefaultResponse {
  success: boolean;
  errorMessage: string;
}

export interface AxiosError {
  errorMessage: string;
  errorCode: string;
  data: ApiError | unknown;
}

export interface ApiError {
  statusCode: number;
  type: ApiException;
  message: string;
  source: 'anoud' | 'core' | 'bff';
  data?: { errorCode?: string; errors?: Record<string, string[]> };
}

// API BUILDER TYPES
export interface TransportConfig {
  default?: boolean;
  version: string;
  interceptors?: AxiosInterceptorOptions;
}

export interface APIConfig {
  protocol?: ApiTransport;
  version?: string;
  serviceConstructorArgs?: unknown[];
}
